import axios from 'axios';
import { config } from '../config.js';
import { useAuthStore } from '../stores/authStore.js';

const api = axios.create({
  baseURL: config.apiBaseUrl,
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const authStore = useAuthStore.getState();
    const token = authStore.getToken();

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Ensure proper headers for FormData uploads: let the browser set the multipart boundary
    if (config.data instanceof FormData) {
      if (config.headers && config.headers['Content-Type']) {
        delete config.headers['Content-Type'];
      }
    } else {
      // Default JSON content type when not sending FormData
      if (config.headers && !config.headers['Content-Type']) {
        config.headers['Content-Type'] = 'application/json';
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// On desktop the Tauri shell and the Python sidecar start at the same time,
// so the first few requests can race and hit ECONNREFUSED before uvicorn
// has bound port 8765. Retry network failures with a short backoff so the
// boot race resolves itself instead of surfacing an error banner to the user.
const NETWORK_RETRY_MAX = 8;
const NETWORK_RETRY_BASE_MS = 250;
const NETWORK_RETRY_CAP_MS = 2000;

const isNetworkError = (error) =>
  !error.response && (error.code === 'ERR_NETWORK' || error.message === 'Network Error');

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 401) {
      const { logout } = useAuthStore.getState();
      // logout()
    }

    const config = error.config;
    if (config && isNetworkError(error)) {
      config.__retryCount = (config.__retryCount || 0) + 1;
      if (config.__retryCount <= NETWORK_RETRY_MAX) {
        const delay = Math.min(
          NETWORK_RETRY_CAP_MS,
          NETWORK_RETRY_BASE_MS * 2 ** (config.__retryCount - 1),
        );
        await new Promise((resolve) => setTimeout(resolve, delay));
        return api.request(config);
      }
    }

    return Promise.reject(error);
  },
);

export const apiService = {
  // GET request
  get: (url, config = {}) => api.get(url, config),

  // POST request
  post: (url, data, config = {}) => api.post(url, data, config),

  // PUT request
  put: (url, data, config = {}) => api.put(url, data, config),

  // DELETE request
  delete: (url, config = {}) => api.delete(url, config),

  // PATCH request
  patch: (url, data, config = {}) => api.patch(url, data, config),
};

export default api;
